/**
 * Canonical disclosure identity resolver (mirrors PHP VsmeDisclosureIdResolver).
 */
import disclosuresJson from '../../../config/vsme_disclosures.json';
import migrationJson from '../../../config/canonical_id_migration.json';
import { formatDisplayRefFromStepId } from './normRefs';

type DisclosureEntryV2 = {
  id: string;
  norm: {
    module: string;
    paragraph: number | null;
    points: string[];
    ref: string | null;
  };
  aliases: {
    legacy: string[];
    form_fields: string[];
    schema_steps?: string[];
  };
  display: {
    title: string | null;
    workfile_title?: string | null;
    description: string[];
    anchors: string[];
  };
};

export type DisclosureEntry = {
  id: string;
  module: string;
  paragraph: number | null;
  points: string[];
  legacy_ids: string[];
  form_fields: string[];
  ref: string | null;
  title: string | null;
  workfile_title?: string | null;
  description: string[];
  anchors: string[];
};

type StepBinding = {
  step_id: string;
  disclosure_ids: string[];
  form_field_ids: string[];
};

type DisclosuresFile = {
  version?: string;
  composite_targets?: Record<string, string[]>;
  step_bindings?: StepBinding[];
  disclosures: DisclosureEntryV2[] | DisclosureEntry[];
};

type MigrationFile = {
  schema_steps?: Record<string, string>;
  retired_canonical_aliases?: Record<string, string>;
  datapoints?: Record<string, string>;
};

const disclosuresFile = disclosuresJson as DisclosuresFile;
const migrationFile = migrationJson as MigrationFile;

const normalizeEntry = (entry: DisclosureEntryV2 | DisclosureEntry): DisclosureEntry => {
  if ('norm' in entry && entry.norm) {
    return {
      id: entry.id,
      module: entry.norm.module,
      paragraph: entry.norm.paragraph,
      points: entry.norm.points ?? [],
      legacy_ids: entry.aliases?.legacy ?? [],
      form_fields: entry.aliases?.form_fields ?? [],
      ref: entry.norm.ref,
      title: entry.display?.title ?? null,
      workfile_title: entry.display?.workfile_title,
      description: entry.display?.description ?? [],
      anchors: entry.display?.anchors ?? [],
    };
  }

  return entry as DisclosureEntry;
};

const compositeTargets = new Map<string, string[]>();
for (const [targetId, canonicals] of Object.entries(disclosuresFile.composite_targets ?? {})) {
  compositeTargets.set(
    targetId.toUpperCase(),
    canonicals.map((id) => id.toLowerCase())
  );
}

const byCanonicalId = new Map<string, DisclosureEntry>();
const aliasToCanonical = new Map<string, string>();

for (const rawEntry of disclosuresFile.disclosures ?? []) {
  const entry = normalizeEntry(rawEntry);
  const canonical = entry.id.toLowerCase();
  byCanonicalId.set(canonical, entry);
  aliasToCanonical.set(canonical, canonical);
  for (const legacy of entry.legacy_ids ?? []) {
    aliasToCanonical.set(legacy.toLowerCase(), canonical);
    aliasToCanonical.set(legacy.toUpperCase(), canonical);
  }
  for (const field of entry.form_fields ?? []) {
    aliasToCanonical.set(field.toLowerCase(), canonical);
  }
}

for (const section of [
  migrationFile.retired_canonical_aliases,
  migrationFile.datapoints,
  migrationFile.schema_steps,
] as const) {
  if (!section) {
    continue;
  }
  for (const [from, to] of Object.entries(section)) {
    if (typeof from !== 'string' || typeof to !== 'string') {
      continue;
    }
    const alias = from.toLowerCase().trim();
    const canonical = to.toLowerCase().trim();
    if (alias === '' || canonical === '' || aliasToCanonical.has(alias)) {
      continue;
    }
    aliasToCanonical.set(alias, canonical);
  }
}

const hasFieldBearingEntry = (entry: DisclosureEntry | undefined): boolean =>
  (entry?.form_fields ?? []).some((field) => field.trim() !== '');

export function resolveCanonicalId(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(/^field-/i, '').toLowerCase();
  const hit = aliasToCanonical.get(normalized);
  if (hit) return hit;

  const upper = trimmed.replace(/^field-/i, '').toUpperCase();
  const upperHit = aliasToCanonical.get(upper.toLowerCase());
  if (upperHit) return upperHit;

  if (byCanonicalId.has(normalized)) {
    return normalized;
  }

  return null;
}

const resolveFieldBearingCanonicalIds = (
  canonical: string,
  visited: Set<string>
): string[] => {
  const entry = byCanonicalId.get(canonical);
  if (!entry || hasFieldBearingEntry(entry)) {
    return [canonical];
  }

  if (visited.has(canonical)) {
    return [canonical];
  }
  visited.add(canonical);

  const expanded: string[] = [];
  for (const legacy of entry.legacy_ids ?? []) {
    for (const candidate of resolveCanonicalIdsForTargetInternal(legacy, visited)) {
      if (candidate !== canonical) {
        expanded.push(candidate);
      }
    }
    for (const candidate of resolvePartialLegacyComposite(legacy)) {
      if (candidate !== canonical) {
        expanded.push(candidate);
      }
    }
  }

  return expanded.length > 0 ? [...new Set([canonical, ...expanded])] : [canonical];
};

const resolvePartialLegacyComposite = (trimmed: string): string[] => {
  const compositeLegacy = trimmed.match(/^([BC]\d+)-([\d-]+)$/i);
  if (!compositeLegacy) {
    return [];
  }

  const prefix = compositeLegacy[1].toUpperCase();
  const numbers = compositeLegacy[2]
    .split('-')
    .map((number) => number.trim())
    .filter(Boolean);
  if (numbers.length <= 1) {
    return [];
  }

  const atomic: string[] = [];
  for (const number of numbers) {
    const canonical = resolveCanonicalId(`${prefix}-${number}`);
    if (canonical) {
      atomic.push(canonical);
    }
  }

  return atomic;
};

export function resolveLegacyCriterionId(id: string): string | null {
  const entry = getDisclosureEntry(id);
  if (!entry?.legacy_ids?.length) return null;
  return entry.legacy_ids[0]?.toUpperCase() ?? null;
}

export function getDisclosureEntry(id: string): DisclosureEntry | undefined {
  const canonical = resolveCanonicalId(id);
  if (!canonical) return undefined;
  return byCanonicalId.get(canonical);
}

export function resolveWorkfileTitle(id: string): string | null {
  const workfile = getDisclosureEntry(id)?.workfile_title?.trim();
  return workfile ? workfile : null;
}

const resolveCanonicalIdsForTargetInternal = (
  targetId: string,
  visited: Set<string>
): string[] => {
  const trimmed = targetId.trim();
  if (!trimmed) return [];

  const legacyRange = trimmed.match(/^([BC]\d+)-(\d+(?:-\d+)+)$/i);
  if (legacyRange) {
    const prefix = legacyRange[1].toUpperCase();
    const numbers = legacyRange[2].split('-');
    const atomic: string[] = [];
    for (const number of numbers) {
      const trimmedNumber = number.trim();
      if (trimmedNumber === '') {
        continue;
      }
      const canonical = resolveCanonicalId(`${prefix}-${trimmedNumber}`);
      if (canonical) {
        atomic.push(canonical);
      }
    }
    const nonEmpty = numbers.filter((number) => number.trim() !== '');
    if (atomic.length > 0 && atomic.length === nonEmpty.length) {
      return [...new Set(atomic)];
    }
  }

  const composite = compositeTargets.get(trimmed.toUpperCase());
  if (composite?.length) {
    const expanded: string[] = [];
    for (const canonical of composite) {
      for (const resolved of resolveFieldBearingCanonicalIds(canonical, visited)) {
        expanded.push(resolved);
      }
    }
    return [...new Set(expanded)];
  }

  const stepBindings = disclosuresFile.step_bindings ?? [];
  for (const binding of stepBindings) {
    if (binding.step_id.toUpperCase() !== trimmed.toUpperCase()) continue;
    const resolved: string[] = [];
    for (const canonical of binding.disclosure_ids ?? []) {
      for (const id of resolveFieldBearingCanonicalIds(canonical, visited)) {
        resolved.push(id);
      }
    }
    if (resolved.length) return [...new Set(resolved)];
  }

  if (trimmed.includes('__')) {
    const all: string[] = [];
    for (const part of trimmed.split('__')) {
      for (const canonical of resolveCanonicalIdsForTargetInternal(part, visited)) {
        all.push(canonical);
      }
    }
    return [...new Set(all)];
  }

  if (trimmed.includes('_') && /^[BC]\d+/i.test(trimmed)) {
    const all: string[] = [];
    for (const part of trimmed.split('_')) {
      for (const canonical of resolveCanonicalIdsForTargetInternal(part, visited)) {
        all.push(canonical);
      }
    }
    return [...new Set(all)];
  }

  const paragraphStepComposite = trimmed.match(/^([bc]\d+p\d+(?:-[bc]\d+)?)-([\d-]+)$/i);
  if (paragraphStepComposite) {
    const moduleMatch = paragraphStepComposite[1].match(/^([bc])(\d+)p/i);
    if (moduleMatch) {
      const prefix = `${moduleMatch[1].toUpperCase()}${moduleMatch[2]}`;
      const numbers = paragraphStepComposite[2]
        .split('-')
        .map((n) => n.trim())
        .filter(Boolean);
      if (numbers.length > 1) {
        const atomic: string[] = [];
        for (const number of numbers) {
          const canonical = resolveCanonicalId(`${prefix}-${number}`);
          if (canonical) {
            atomic.push(canonical);
          }
        }
        if (atomic.length === numbers.length) {
          return atomic;
        }
      }
    }
  }

  const paragraphMultiCriterion = trimmed.match(/^(b\d+p\d+(?:-[bc]\d+)?)-(\d+(?:-\d+)*)$/i);
  if (paragraphMultiCriterion) {
    const prefix = paragraphMultiCriterion[1].toLowerCase();
    const numbers = paragraphMultiCriterion[2].split('-').filter(Boolean);
    if (numbers.length > 1) {
      const atomic = numbers.map((number) => `${prefix}-${number}`);
      if (atomic.every((id) => resolveCanonicalId(id) !== null)) {
        return atomic;
      }
    }
  }

  const compositeLegacy = trimmed.match(/^([BC]\d+)-([\d-]+)$/i);
  if (compositeLegacy) {
    const prefix = compositeLegacy[1].toUpperCase();
    const numbers = compositeLegacy[2].split('-').map((n) => n.trim()).filter(Boolean);
    if (numbers.length > 1) {
      const atomic: string[] = [];
      for (const number of numbers) {
        const canonical = resolveCanonicalId(`${prefix}-${number}`);
        if (canonical) atomic.push(canonical);
      }
      if (atomic.length === numbers.length) {
        return atomic;
      }
    }
  }

  const single = resolveCanonicalId(trimmed);
  if (single) return resolveFieldBearingCanonicalIds(single, visited);

  const partialLegacy = resolvePartialLegacyComposite(trimmed);
  if (partialLegacy.length > 0) {
    return partialLegacy;
  }

  const legacySuffix = trimmed.match(/^([BC]\d+)-(.+)$/i);
  if (legacySuffix) {
    const prefix = legacySuffix[1].toUpperCase();
    const canonicals: string[] = [];
    for (const number of legacySuffix[2].split('-')) {
      const trimmedNumber = number.trim();
      if (trimmedNumber === '') {
        continue;
      }
      const canonical = resolveCanonicalId(`${prefix}-${trimmedNumber}`);
      if (canonical) {
        canonicals.push(canonical);
      }
    }
    if (canonicals.length > 0) {
      return canonicals;
    }
  }

  return [];
};

export function resolveCanonicalIdsForTarget(targetId: string): string[] {
  return resolveCanonicalIdsForTargetInternal(targetId, new Set<string>());
}

export function formatNormDisplayRef(module: string, ref: string): string {
  const formattedRef = ref.replace(/§(\S)/g, '§ $1');
  return `${module} ${formattedRef}`;
}

export type DisclosureLabel = {
  disclosureId: string;
  legacyCriterionId: string | null;
  ref: string | null;
  displayRef: string;
  title: string | null;
  label: string;
  anchors: string[];
};

export function resolveDisclosureLabel(
  id: string,
  explicitTitle?: string | null
): DisclosureLabel {
  const canonical = resolveCanonicalId(id);
  if (!canonical) {
    const legacy = id.replace(/^field-/i, '').toUpperCase();
    const title = explicitTitle?.trim() ?? null;
    return {
      disclosureId: legacy,
      legacyCriterionId: legacy || null,
      ref: null,
      displayRef: legacy,
      title,
      label: title ? `${legacy} - ${title}` : legacy,
      anchors: [],
    };
  }

  const entry = byCanonicalId.get(canonical);
  if (!entry) {
    return {
      disclosureId: canonical,
      legacyCriterionId: null,
      ref: null,
      displayRef: canonical,
      title: explicitTitle ?? null,
      label: canonical,
      anchors: [],
    };
  }

  const ref = entry.ref ?? null;
  // Visible § derives from the id (uniformly 2026); legacy ref strings are a mix.
  const displayRef = /^[bc]\d+p\d+/.test(canonical)
    ? formatDisplayRefFromStepId(canonical)
    : ref && entry.module
      ? formatNormDisplayRef(entry.module, ref)
      : canonical;
  const fromExplicit = explicitTitle?.trim() ?? '';
  const entryTitle = entry.title?.trim() ?? '';
  const title = fromExplicit !== '' ? fromExplicit : entryTitle || null;
  const label = title ? `${displayRef} : ${title}` : displayRef;

  return {
    disclosureId: canonical,
    legacyCriterionId: resolveLegacyCriterionId(canonical),
    ref,
    displayRef,
    title,
    label,
    anchors: [...(entry.anchors ?? [])],
  };
}

export function getDisclosureDisplayLabel(
  id: string,
  explicitTitle?: string | null
): string {
  return resolveDisclosureLabel(id, explicitTitle).label;
}

/** Read norm criteria value by canonical or legacy disclosure id. */
export function getNormCriteriaValue(
  criteria: Record<string, unknown>,
  id: string
): unknown {
  const keys = new Set<string>([id.trim()]);
  const canonical = resolveCanonicalId(id);
  if (canonical) {
    keys.add(canonical);
  }
  const legacy = resolveLegacyCriterionId(id);
  if (legacy) {
    keys.add(legacy);
  }
  keys.add(id.replace(/^field-/i, '').toUpperCase());

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(criteria, key)) {
      return criteria[key];
    }
  }

  return undefined;
}
